import React from 'react';
import {
  Box,
  Paper,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  Typography,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const LayerTogglePanel = ({
  showFilters,
  layerTree,
  layerVisibility,
  expandedNodes,
  setExpandedNodes,
  setLayerVisibility,
  mapRef,
  enable3D,
  setEnable3D
}) => {
  const toggleSourceLayers = (sourceId, visible) => {
    const map = mapRef.current;
    if (!map || !layerTree[sourceId]) return;

    layerTree[sourceId].layers.forEach(layerId => {
      if (layerId.includes('3d') || layerId === 'building-3d') {
        const shouldShow = visible && enable3D;
        map.setLayoutProperty(layerId, 'visibility', shouldShow ? 'visible' : 'none');
      } else {
        map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
      }
    });

    // Update visibility state for all layers in this source
    const newVisibility = {};
    layerTree[sourceId].layers.forEach(layerId => {
      newVisibility[layerId] = visible;
    });
    setLayerVisibility(prev => ({ ...prev, ...newVisibility }));
  };

  const toggle3D = () => {
    const map = mapRef.current;
    if (!map) return;

    const new3DState = !enable3D;
    setEnable3D(new3DState);

    // Toggle 3D layers visibility
    if (new3DState) {
      // Enable 3D layers
      map.setLayoutProperty('building-3d', 'visibility', 'visible');
      // Enable 3D GeoJSON layers
      Object.keys(layerTree).forEach(sourceId => {
        const source = layerTree[sourceId];
        if (source.children) {
          Object.values(source.children).forEach(child => {
            child.layers.forEach(layerId => {
              if (layerId.includes('3d')) {
                map.setLayoutProperty(layerId, 'visibility', 'visible');
              }
            });
          });
        }
      });
    } else {
      // Disable 3D layers
      map.setLayoutProperty('building-3d', 'visibility', 'none');
      // Disable 3D GeoJSON layers
      Object.keys(layerTree).forEach(sourceId => {
        const source = layerTree[sourceId];
        if (source.children) {
          Object.values(source.children).forEach(child => {
            child.layers.forEach(layerId => {
              if (layerId.includes('3d')) {
                map.setLayoutProperty(layerId, 'visibility', 'none');
              }
            });
          });
        }
      });
    }

    // Adjust map pitch based on 3D state
    map.setPitch(new3DState ? 60 : 0);
  };

  if (!showFilters) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 70,
        right: 16,
        zIndex: 102,
        p: 2,
        borderRadius: 2,
        minWidth: 250,
        maxHeight: '70vh',
        overflow: 'auto'
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Layers
      </Typography>

      {/* Layer Tree Component */}
      <Box>
        {Object.entries(layerTree).map(([sourceId, source]) => (
          <Box key={sourceId} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => {
                  const isExpanded = expandedNodes.includes(sourceId);
                  if (isExpanded) {
                    setExpandedNodes(expandedNodes.filter(id => id !== sourceId));
                  } else {
                    setExpandedNodes([...expandedNodes, sourceId]);
                  }
                }}
              >
                {expandedNodes.includes(sourceId) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
              <Checkbox
                size="small"
                checked={source.layers.every(layerId => layerVisibility[layerId])}
                indeterminate={source.layers.some(layerId => layerVisibility[layerId]) && !source.layers.every(layerId => layerVisibility[layerId])}
                onChange={(e) => toggleSourceLayers(sourceId, e.target.checked)}
              />
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {source.label}
              </Typography>
            </Box>

            {expandedNodes.includes(sourceId) && (
              <Box sx={{ ml: 3 }}>
                {Object.entries(source.children || {}).map(([childId, child]) => (
                  <Box key={childId} sx={{ mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {child.children && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const isExpanded = expandedNodes.includes(childId);
                            if (isExpanded) {
                              setExpandedNodes(expandedNodes.filter(id => id !== childId));
                            } else {
                              setExpandedNodes([...expandedNodes, childId]);
                            }
                          }}
                        >
                          {expandedNodes.includes(childId) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>
                      )}
                      <Checkbox
                        size="small"
                        checked={child.layers.every(layerId => layerVisibility[layerId])}
                        indeterminate={child.layers.some(layerId => layerVisibility[layerId]) && !child.layers.every(layerId => layerVisibility[layerId])}
                        onChange={(e) => {
                          child.layers.forEach(layerId => {
                            if (layerId.includes('3d') || layerId === 'building-3d') {
                              const shouldShow = e.target.checked && enable3D;
                              mapRef.current?.setLayoutProperty(layerId, 'visibility', shouldShow ? 'visible' : 'none');
                            } else {
                              mapRef.current?.setLayoutProperty(layerId, 'visibility', e.target.checked ? 'visible' : 'none');
                            }
                            setLayerVisibility(prev => ({ ...prev, [layerId]: e.target.checked }));
                          });
                        }}
                      />
                      <Typography variant="body2">
                        {child.label}
                      </Typography>
                    </Box>

                    {expandedNodes.includes(childId) && child.children && (
                      <Box sx={{ ml: 3 }}>
                        {Object.entries(child.children).map(([subChildId, subChild]) => (
                          <Box key={subChildId} sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Checkbox
                                size="small"
                                checked={subChild.layers.every(layerId => layerVisibility[layerId])}
                                indeterminate={subChild.layers.some(layerId => layerVisibility[layerId]) && !subChild.layers.every(layerId => layerVisibility[layerId])}
                                onChange={(e) => {
                                  subChild.layers.forEach(layerId => {
                                    if (layerId.includes('3d') || layerId === 'building-3d') {
                                      const shouldShow = e.target.checked && enable3D;
                                      mapRef.current?.setLayoutProperty(layerId, 'visibility', shouldShow ? 'visible' : 'none');
                                    } else {
                                      mapRef.current?.setLayoutProperty(layerId, 'visibility', e.target.checked ? 'visible' : 'none');
                                    }
                                    setLayerVisibility(prev => ({ ...prev, [layerId]: e.target.checked }));
                                  });
                                }}
                              />
                              <Typography variant="body2">
                                {subChild.label}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={enable3D}
              onChange={toggle3D}
            />
          }
          label="Enable 3D View"
        />
      </FormGroup>
    </Paper>
  );
};

export default LayerTogglePanel; 